import constants from 'app/constants'
import SimpleComponent from 'lib/SimpleComponent'
import { isTouch } from 'lib/support'
import imageFactory from './imageFactory'

/** @type {Map} */
const CACHED_COMPOSITES = new Map()

/**
 * The `CompositeImageCanvasRenderer` is used to render composite images.
 * Specifically it was created to assist in rendering the pre-fabricated
 * avatar bases but it can ultimately be used to render any avatar
 * at a supplied size.
 *
 * This works by efficiently loading avatar images into separate layers,
 * and utilizes caching to minimize the impact of loading the same
 * data multiple times, as well as caching the resulting output based
 * on the serialized input
 */
export default class CompositeImageCanvasRenderer extends SimpleComponent {
  constructor (props) {
    super(props)

    /** @type {object} */
    this.canvas = this.getCanvas()
  }

  /**
   * Get the canvas
   *
   * @returns {object} the canvas node and context
   * @private
   */
  getCanvas () {
    const node = document.createElement('canvas')
    const context = node.getContext('2d')

    return {
      node,
      context
    }
  }

  /**
   * Renders a canvas with the supplied layers at
   * the given size
   *
   * @param {Array} layers - the image layers to render
   * @param {number} size - the canvas size
   * @returns {HTMLCanvasElement} the canvas
   * @private
   */
  renderCanvas (layers, size) {
    const { context, node } = this.canvas

    node.width = size
    node.height = size

    layers.forEach(image => {
      context.drawImage(image, 0, 0, size, size)
    })

    return node
  }

  /**
   * Render
   *
   * @param {object} layers - the layers in a `key: imageName` format
   * @param {number} size - the size to render at
   */
  async render (layers, size = 200) {
    const res = []
    const serialized = JSON.stringify({ layers, size })
    const cachedMatch = CACHED_COMPOSITES.get(serialized)

    if (cachedMatch) {
      // Use the serialized data to check against the `CACHED_COMPOSITES`
      return cachedMatch
    }

    for (let i = 0, len = layers.length; i < len; i += 1) {
      const src = layers[i]

      if (!src) {
        // skip
        continue
      }

      const url = `${constants.PATH_CDN}/tr:w-${size}/${src}?v=${constants.CACHE_BUST_VERSION}`
      const img = await imageFactory.load(url)

      if (img) {
        // Add the image to the result
        res.push(img)
      }
    }

    const output = this.renderCanvas(res, size).toDataURL()

    if (!isTouch) {
      // Add to cache
      CACHED_COMPOSITES.set(serialized, output)
    }

    return output
  }
}
